import { lazy, startTransition, useMemo, useRef, useState } from "react"

import { CRONOFY_WIDGET } from "$/constants"
import { axiosClient } from "$/http"
import { getErrorMessages } from "$/utils/get-error-messages"

import {
    Button,
    ETypographyAlignment,
    ETypographyColor,
    ETypographyFamily,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    Typography
} from "@/3514/components"
import { type TScreenBreakpoints, useScreenBreakpoints } from "@/3514/hooks"
import { minutes } from "@/3514/utils/time.utils"
import Urls from "@/api/urls"
import { useAvailability } from "@/entities/coach-calendar/api/use-availavility-rules-attrs"
import { useAvailabilityConfirmRules } from "@/entities/coach-calendar/api/use-confirm"
import { WarningMessage } from "@/shared/WarningMessage"
import { FullSpinner } from "@/shared/spinner"

import "./styles.scss"

const LazyCronofyWidget = lazy(() => import("$/components/CronofyWidgets/CronofyWidget"))

/**
 * @deprecated legacy piece of sh1t
 */
const CoachAvailability = ({ classes }) => {
    const { data: availabilityRules, isLoading: isLoadingAvailabilityRules } = useAvailability({
        staleTime: minutes(20),
        gcTime: minutes(20),
        refetchOnMount: true,
        refetchOnWindowFocus: "always",
        experimental_prefetchInRender: true
    })
    const { data: availabilityConfirm, isLoading: isLoadingAvailabilityConfirm } = useAvailabilityConfirmRules({
        staleTime: minutes(20),
        gcTime: minutes(20),
        refetchOnMount: true,
        refetchOnWindowFocus: "always",
        experimental_prefetchInRender: true
    })
    const viewerRef = useRef<HTMLDivElement>(null)
    const [rules, setRules] = useState(null)
    const [isEdit, setIsEdit] = useState(false)
    const [isBusy, setIsBusy] = useState(false)
    const [error, setError] = useState(null)

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const isMobile: boolean = breakpoints.xs || breakpoints.sm

    const onAvailabilitySelect = async data => {
        if (data) {
            try {
                setRules(data)
            } catch (e) {
                setError(getErrorMessages(e))
            }
        }
    }

    const onSaveAvailability = async () => {
        if (rules) {
            try {
                setIsBusy(true)
                const btn = viewerRef?.current?.querySelector(".av__submit") as HTMLButtonElement
                btn?.click()
                await axiosClient.post(Urls.avRules(), rules.availability_rule)

                startTransition((): void => setIsEdit(false))
            } catch (e) {
                setError(getErrorMessages(e))
            } finally {
                startTransition((): void => setIsBusy(false))
            }
        }
    }

    const AvailabilityEdit = useMemo(() => {
        return isLoadingAvailabilityRules ? (
            <FullSpinner />
        ) : (
            <LazyCronofyWidget
                ref={viewerRef}
                widget={CRONOFY_WIDGET.AvailabilityRules}
                key={CRONOFY_WIDGET.AvailabilityRules}
                id="availability-rules-calendar"
                options={{
                    ...(availabilityRules as any),
                    styles: {
                        prefix: "av"
                    },
                    callback: onAvailabilitySelect
                }}
            />
        )
    }, [isLoadingAvailabilityRules, availabilityRules, isEdit])

    const AvailabilityHours = useMemo(() => {
        const config = {
            ...(availabilityConfirm ?? {}),
            callback: () => {}
        }
        return isLoadingAvailabilityConfirm ? (
            <FullSpinner />
        ) : (
            <LazyCronofyWidget
                widget={CRONOFY_WIDGET.AvailabilityViewer}
                id="av-viewer"
                options={config}
                key={CRONOFY_WIDGET.AvailabilityViewer}
            />
        )
    }, [availabilityConfirm, isLoadingAvailabilityConfirm, isEdit])

    return (
        <div className={classes.container()}>
            <div className={classes.headingWrapper()}>
                <Typography
                    text={isEdit ? "Edit Your Availability" : "My Availability"}
                    alignment={isMobile ? ETypographyAlignment.Center : ETypographyAlignment.Left}
                    tag={ETypographyTag.Heading1}
                    weight={ETypographyWeight.Medium}
                    size={isMobile ? ETypographySize.Heading2 : ETypographySize.Heading1}
                    family={ETypographyFamily.DmSerif}
                />

                <Typography
                    text="This is the availability that will be visible to your 1:1 coaching participants."
                    alignment={isMobile ? ETypographyAlignment.Center : ETypographyAlignment.Left}
                    size={ETypographySize.Small}
                    color={ETypographyColor.Dark}
                />
            </div>

            {isEdit ? AvailabilityEdit : AvailabilityHours}

            <div className={classes.headingWrapper()}>
                <Typography
                    text="Need to adjust your working hours?"
                    alignment={isMobile ? ETypographyAlignment.Center : ETypographyAlignment.Left}
                    tag={ETypographyTag.Heading2}
                    weight={ETypographyWeight.Medium}
                    size={isMobile ? ETypographySize.Medium : ETypographySize.Heading2}
                    family={ETypographyFamily.DmSerif}
                />

                <Typography
                    text="If your regular working hours have changed, use the button below to adjust them accordingly (ex. I’m no longer available on Fridays, or I’m unavailable every day after 3pm). Our system will check the free time on your linked calendar(s) against the working hours you’ve set in order to determine the scheduling availability that appears for participants."
                    alignment={isMobile ? ETypographyAlignment.Center : ETypographyAlignment.Left}
                    size={ETypographySize.Small}
                    color={ETypographyColor.Black}
                />
            </div>

            <div className={classes.buttonWrapper()}>
                <WarningMessage message={error} />

                {isEdit ? (
                    <Button isCentered={isMobile} onClick={onSaveAvailability} isBusy={isBusy}>
                        Save
                    </Button>
                ) : (
                    <Button
                        isCentered={isMobile}
                        onClick={() =>
                            startTransition((): void => {
                                setIsEdit(true)
                                window.scrollTo({
                                    top: 0,
                                    behavior: "smooth"
                                })
                            })
                        }
                    >
                        Edit Working Hours
                    </Button>
                )}
            </div>
        </div>
    )
}

export default CoachAvailability
